// src/components/common/IconPicker.jsx
import React, { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import ToolIcon, { ICON_NAMES } from './ToolIcon';
import { cx } from './ui';

const IconPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICON_NAMES;
    return ICON_NAMES.filter((name) => name.toLowerCase().includes(q));
  }, [query]);

  return (
    <div className="rounded-lg border border-line-strong bg-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-2 text-left"
      >
        <span className="grid h-9 w-9 flex-none place-items-center rounded-[9px] bg-accent-soft text-accent">
          <ToolIcon name={value} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-[13px] text-ink">{value}</span>
          <span className="block text-xs text-ink-3">
            {open ? 'Pick one below' : 'Change icon'}
          </span>
        </span>
        <ChevronDown
          className={cx('h-4 w-4 flex-none text-ink-3 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="border-t border-line p-3">
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${ICON_NAMES.length} icons…`}
              className="w-full rounded-lg border border-line-strong bg-surface py-1.5 pl-9 pr-3 text-sm placeholder:text-ink-3 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-soft"
            />
          </div>

          {results.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-3">
              No icon called “{query}”.
            </p>
          ) : (
            <div className="grid max-h-56 grid-cols-8 gap-1 overflow-y-auto">
              {results.map((name) => (
                <button
                  key={name}
                  type="button"
                  title={name}
                  aria-label={name}
                  aria-pressed={name === value}
                  onClick={() => {
                    onChange(name);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={cx(
                    'grid aspect-square place-items-center rounded-md text-ink-2 transition-colors',
                    name === value
                      ? 'bg-accent-soft text-accent ring-1 ring-accent-line'
                      : 'hover:bg-surface-2 hover:text-ink'
                  )}
                >
                  <ToolIcon name={name} className="h-[18px] w-[18px]" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IconPicker;
